import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatAmountInWords } from '../../../utils/amountInWords';

const SaleInvoicePreview = ({ data, isLoading }) => {
    const [searchParams] = useSearchParams();
    const preview = searchParams.get('preview');
    const [showPrintModal, setShowPrintModal] = useState(false);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const formatCurrency = (amount) => {
        if (amount === undefined || amount === null || amount === '') return '0.00';
        return parseFloat(amount).toFixed(3);
    };

    const getText = (value, preferredLang = 'en') => {
        if (!value) return '';
        if (typeof value === 'string') return value;
        if (typeof value === 'object') {
            if (value[preferredLang]) return value[preferredLang];
            const first = Object.values(value)[0];
            return typeof first === 'string' ? first : '';
        }
        return String(value);
    };

    useEffect(() => {
        if (!data) return;
        if (preview) setShowPrintModal(true);
    }, [preview, data]);

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="text-center p-4">
                <h5>No data available</h5>
            </div>
        );
    }

    const handlePrint = () => setShowPrintModal(true);
    const handleClosePrintModal = () => setShowPrintModal(false);

    return (
        <div className="h-100 d-flex flex-column">
            <div className="d-flex justify-content-between mb-2">
                <div className="d-flex justify-content-start">
                    <h5 className="mb-0">Sale Invoice Details</h5>
                </div>
                <div className="d-flex justify-content-end">
                    <button className="btn btn-primary btn-sm no-print" onClick={handlePrint} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <i className="fas fa-print"></i>
                        Print Preview
                    </button>
                </div>
            </div>

            {/* Basic on-screen preview can be minimal; print modal renders full invoice */}
            <div className="table-responsive mt-2">
                <table className="table table-sm table-borderless" style={{ marginBottom: 0 }}>
                    <tbody>
                        <tr>
                            <td className="fw-bold">Invoice No:</td>
                            <td>{getText(data.invoice_number) || getText(data.number) || '-'}</td>
                            <td className="fw-bold">Invoice Date:</td>
                            <td>{formatDate(data.invoice_date || data.date)}</td>
                        </tr>
                        <tr>
                            <td className="fw-bold">Customer:</td>
                            <td>{getText(data.customer_name) || getText(data.customer?.label) || '-'}</td>
                            <td className="fw-bold">VAT No:</td>
                            <td>{getText(data.vat_number) || getText(data.customer?.vat_number) || '-'}</td>
                        </tr>
                        <tr>
                            <td className="fw-bold">Address:</td>
                            <td colSpan="3">
                                {(getText(data.address1) || '')}
                                {getText(data.address2) ? ` ${getText(data.address2)}` : ''}
                                {getText(data.country?.name) || data.country?.label ? `, ${getText(data.country?.name) || data.country?.label}` : ''}
                            </td>
                        </tr>
                        <tr>
                            <td className="fw-bold">Phone:</td>
                            <td>{getText(data.phone_number) || getText(data.customer?.phone) || '-'}</td>

                        </tr>
                        <tr>
                            <td className="fw-bold">Delivery Note(s):</td>
                            <td colSpan="3">
                                {Array.isArray(data.linked_delivery_notes) && data.linked_delivery_notes.length > 0
                                    ? data.linked_delivery_notes
                                        .map((dn) => dn.label || dn.invoice_number)
                                        .filter(Boolean)
                                        .join(', ')
                                    : (getText(data.delivery_note?.label) || getText(data.delivery_number) || '-')}
                            </td>
                        </tr>
                        <tr>
                            <td className="fw-bold">LPO No:</td>
                            <td>{getText(data.lpo_number) || '-'}</td>
                            <td className="fw-bold">LPO Date:</td>
                            <td>{formatDate(data.lpo_date)}</td>
                        </tr>
                        <tr>
                            <td className="fw-bold">Customer LPO:</td>
                            <td>{getText(data.customer_lpo) || '-'}</td>
                            <td></td>
                            <td></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* On-screen line items table (similar to CashMemo preview) */}
            <div className="table-responsive mt-4">
                {(() => {
                    const lineItems = Array.isArray(data.grouped_items)
                        ? data.grouped_items
                        : (Array.isArray(data.sale_invoice_items) ? data.sale_invoice_items : []);
                    return (
                        <table className="table table-bordered">
                            <thead>
                                <tr>
                                    <th className="text-nowrap p-3">Item Code</th>
                                    <th className="text-nowrap p-3">Item Name</th>
                                    <th className="text-nowrap p-3">Unit</th>
                                    <th className="text-nowrap p-3">Qty</th>
                                    <th className="text-nowrap p-3">Unit Price</th>
                                    <th className="text-nowrap p-3">Discount %</th>
                                    <th className="text-nowrap p-3">Amt Excl VAT</th>
                                    <th className="text-nowrap p-3">VAT %</th>
                                    <th className="text-nowrap p-3">VAT Amt</th>
                                    <th className="text-nowrap p-3">Amt Incl VAT</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lineItems && lineItems.length > 0 ? (
                                    lineItems.map((row, index) => {
                                        const code = getText(row.item?.item_code) || getText(row.item_code) || 'N/A';
                                        const name = getText(row.item_description) || 'N/A';
                                        const unit = getText(row.unit) || 'N/A';
                                        const qty = row.quantity ?? row.qty ?? 0;
                                        const price = row.unit_price ?? row.price ?? 0;
                                        const disc = row.discount_percentage ?? row.discount_percent ?? 0;
                                        const excl = (row.amount_excl_vat ?? row.amount_excluding_vat ?? row.line_amount ?? 0);
                                        const vatp = row.vat_percentage ?? row.vat_percent ?? 0;
                                        const vatAmt = row.vat_amount ?? 0;
                                        const incl = row.amount_incl_vat ?? row.line_total ?? (excl + vatAmt);
                                        return (
                                            <tr key={index}>
                                                <td className="text-nowrap p-3 align-middle">{code}</td>
                                                <td className="text-nowrap p-3 align-middle">{name}</td>
                                                <td className="p-3 align-middle">{unit}</td>
                                                <td className="p-3 align-middle">{qty}</td>
                                                <td className="p-3 align-middle">{formatCurrency(price)}</td>
                                                <td className="p-3 align-middle">{disc || 0}%</td>
                                                <td className="p-3 align-middle">{formatCurrency(excl)}</td>
                                                <td className="p-3 align-middle">{vatp || 0}%</td>
                                                <td className="p-3 align-middle">{formatCurrency(vatAmt)}</td>
                                                <td className="p-3 align-middle">{formatCurrency(incl)}</td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="10" className="text-center">No items added</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    );
                })()}
            </div>

            {/* Compact totals summary on screen (same fields as print) */}
            <div className="table-responsive mt-2">
                <table className="table table-sm table-borderless" style={{ marginBottom: 0 }}>
                    <tbody>
                        <tr>
                            <td className="fw-bold">Gross Total:</td>
                            <td>{formatCurrency(data.gross_total)}</td>
                            <td className="fw-bold">Discount %:</td>
                            <td>{data.discount_percentage || '0.00'}%</td>
                        </tr>
                        <tr>
                            <td className="fw-bold">Discount Amount:</td>
                            <td>{formatCurrency(data.discount_amount)}</td>
                            <td className="fw-bold">Sub Total:</td>
                            <td>{formatCurrency(data.sub_total)}</td>
                        </tr>
                        <tr>
                            <td className="fw-bold">VAT Amount:</td>
                            <td>{formatCurrency(data.vat_amount)}</td>
                            <td className="fw-bold">Net Total:</td>
                            <td className="fw-bold">{formatCurrency(data.net_total)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {showPrintModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <div style={{ backgroundColor: 'white', width: '90%', height: '90%', borderRadius: '8px', position: 'relative', overflow: 'hidden' }}>
                        <div style={{ padding: '15px', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h5 style={{ margin: 0 }}>Print Preview</h5>
                            <div>
                                <button className="btn btn-primary btn-sm me-2" onClick={() => {
                                    const printWindow = window.open('', '_blank');
                                    if (!printWindow) return;
                                    const baseUrl = window.location.origin + (process.env.PUBLIC_URL || '');
                                    const cssHref = `${baseUrl}/css/invoice_print.css`;
                                    const doPrint = () => {
                                        const modalContent = document.querySelector('.modal-print-content-saleinvoice');
                                        if (modalContent) {
                                            const printContent = modalContent.innerHTML;
                                            const target = printWindow.document.getElementById('print-content');
                                            if (target) target.innerHTML = printContent;
                                        }
                                        printWindow.document.close();
                                        setTimeout(() => { 
                                            printWindow.print(); 
                                            printWindow.close();
                                        }, 100);
                                    };
                                    printWindow.document.write(`
                                        <html>
                                            <head>
                                                <title>Sale Invoice Print</title>
                                                <link rel="stylesheet" href="${cssHref}" id="print-css-link" />
                                            </head>
                                            <body>
                                                <div id="print-content"></div>
                                            </body>
                                        </html>
                                    `);
                                    const linkEl = printWindow.document.getElementById('print-css-link');
                                    if (linkEl) {
                                        const fallback = setTimeout(doPrint, 3000);
                                        const done = () => { clearTimeout(fallback); doPrint(); };
                                        linkEl.onload = done;
                                        linkEl.onerror = done;
                                        if (linkEl.sheet) done();
                                    } else {
                                        doPrint();
                                    }
                                }}>
                                    <i className="fas fa-print me-1"></i>
                                    Print
                                </button>
                                <button className="btn btn-secondary btn-sm" onClick={handleClosePrintModal}>
                                    <i className="fas fa-times me-1"></i>
                                    Close
                                </button>
                            </div>
                        </div>
                        <div style={{ height: 'calc(100% - 70px)', overflow: 'auto', padding: '20px' }} className="modal-print-content-saleinvoice">
                  


                            <div id="container">
                                <div className="print-content">
                                    <div className="clearfix"></div>
                                    <div className="title">
                                        <span>TAX INVOICE</span>
                                    </div>
                                    <section className="middle-box">
                                        <section id="client-info">
                                            <span className="invoice_bill_to_label">{getText(data.customer_code) || getText(data.customer?.code) || ''}</span>
                                            <span className="client-name invoice_client_name">
                                                {getText(data.customer_name) || data.customer?.label || ''}
                                            </span>
                                            <div className="client-address">
                                                <div>{getText(data.address1) || ''}</div>
                                                <div>{getText(data.address2) || ''}</div>
                                            </div>

                                            <section id="client-contacts">
                                                <div>
                                                    <span className="client_label">PHONE : </span>
                                                    <span className="client_value">{getText(data.phone_number) || getText(data.customer?.phone) || '-'}</span>
                                                </div>
                                                <div>
                                                    <span className="client_label">VAT NO : </span>
                                                    <span className="client_value">{data.vat_number || data.customer?.vat_number || ''}</span>
                                                </div>
                                            </section>
                                        </section>

                                        <section id="invoice-info">
                                            <div>
                                                <span className="invoice_label">INVOICE NUMBER : </span>
                                                <span className="invoice_value">{getText(data.invoice_number) || getText(data.number) || ''}</span>
                                            </div>
                                            <div>
                                                <span className="invoice_label">INVOICE DATE : </span>
                                                <span className="invoice_value">{formatDate(data.invoice_date || data.date)}</span>
                                            </div>
                                            <div>
                                                <span className="invoice_label">LPO NO:</span>
                                                <span className="invoice_value">{getText(data.customer_lpo) || getText(data.lpo_number) || '-'}</span>
                                            </div>
                                            <div>
                                                <span className="invoice_label">LPO DATE:</span>
                                                <span className="invoice_value">{formatDate(data.lpo_date)}</span>
                                            </div>
                                        </section>
                                    </section>
                                    <div className="clearfix"></div>
                                    <section id="items">
                                    {(() => {
                                        const lineItems = Array.isArray(data.grouped_items)
                                            ? data.grouped_items
                                            : (Array.isArray(data.sale_invoice_items) ? data.sale_invoice_items : []);
                                        return (
                                            <table cellspacing="0" cellpadding="0">
                                                <thead>
                                                    <tr>
                                                        <th className=""></th>
                                                        <th className="">ITEM </th>
                                                        <th className="invoice_item_unit_label">UNIT</th>
                                                        <th className="invoice_item_quantity_label">QTY</th>
                                                        <th className="invoice_item_price_label">PRICE</th>
                                                        <th className="invoice_item_vat_label">AMT.EXCL VAT</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {lineItems.map((row, idx) => {
                                                        const name = getText(row.item_description) || 'N/A';
                                                        const unit = getText(row.unit) || 'PCS';
                                                        const qty = row.quantity ?? row.qty ?? 0;
                                                        const price = row.unit_price ?? row.price ?? 0;
                                                        const excl = (row.amount_excl_vat ?? row.amount_excluding_vat ?? row.line_amount ?? 0);
                                                        return (
                                                            <tr key={idx} data-iterate="item">
                                                                <td className="invoice_item_row_number" style={{ position: 'relative' }}>
                                                                    {idx + 1}
                                                                </td>
                                                                <td>
                                                                    <span className="">
                                                                        <b>{name}</b>
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <span className="show-mobile invoice_item_unit_label" style={{ display: 'inline' }}
                                                                        contenteditable="true">UNIT</span>
                                                                    <span className="invoice_item_unit" contenteditable="true">{unit}</span>
                                                                </td>
        
                                                                <td>
                                                                    <span className="show-mobile invoice_item_quantity_label" style={{ display: 'inline' }}
                                                                        contenteditable="true">QUANTITY</span>
                                                                    <span className="invoice_item_quantity" contenteditable="true">{qty}</span>
                                                                </td>
                                                                <td>
                                                                    <span className="show-mobile invoice_item_price_label" style={{ display: 'inline' }}
                                                                        contenteditable="true">PRICE :
                                                                    </span>
                                                                    <span className="invoice_item_price" contenteditable="true">{formatCurrency(price)}</span>
                                                                </td>
                                                                <td>
                                                                    <span className="show-mobile invoice_item_vat_label" style={{ display: 'inline' }}
                                                                        contenteditable="true">AMT.EXCL VAT:
                                                                    </span>
                                                                    <span className="invoice_item_vat " contenteditable="true">{formatCurrency(excl)}</span>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        );
                                    })()}
                                    </section>
                                    <div className="clearfix"></div>
                                    <section id="sums">
                                        <table cellspacing="0" cellpadding="0">
                                            <tbody>
                                                <tr className="amount-total">
                                                    <td rowSpan={5} className="invoice_amount_in_words">
                                                        {formatAmountInWords(data.net_total)}
                                                    </td>
                                                    <td className="invoice_amount_subtotal_label " contenteditable="true">
                                                        GROSS AMOUNT
                                                    </td>
                                                    <td className="invoice_amount_subtotal ">{formatCurrency(data.gross_total)}</td>
                                                </tr>
                                                <tr className="amount-total">
                                                    <td className="invoice_amount_subtotal_label">DISCOUNT</td>
                                                    <td className="invoice_amount_subtotal bold">{formatCurrency(data.discount_amount)}</td>
                                                </tr>
                                                <tr className="amount-total">
                                                    <td className="invoice_amount_subtotal_label " contenteditable="true">
                                                        SUB TOTAL
                                                    </td>
                                                    <td className="invoice_amount_subtotal ">{formatCurrency(data.sub_total)}</td>
                                                </tr>
                                                <tr className="amount-total">
                                                    <td className="invoice_amount_subtotal_label " contenteditable="true">
                                                        VAT 10%
                                                    </td>
                                                    <td className="invoice_amount_subtotal ">{formatCurrency(data.vat_amount)}</td>
                                                </tr>
                                                <tr className="amount-total">
                                                    <td className="invoice_amount_subtotal_label bold">NET AMOUNT</td>
                                                    <td className="invoice_amount_subtotal bold">{formatCurrency(data.net_total)}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </section>
                                </div>
                            </div>


                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SaleInvoicePreview;
